import React, { useState, useEffect, useRef } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Grid,
  LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import api from '../services/api';
import SearchableDataTable from '../components/SearchableDataTable';

const AdjustmentRollups = () => {
  const [validAdjustmentCodes, setValidAdjustmentCodes] = useState([]);
  const [adjCodeError, setAdjCodeError] = useState('');
  const [rollups, setRollups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRollup, setCurrentRollup] = useState({
    AdjustmentRollupCode: '',
    AdjustmentRollupName: '',
    AdjustmentRollupType: '',
    AdjustmentList: [],
    Year: new Date().getFullYear(),
    Period: 'Q4',
    ModifiedOn: new Date(),
    ModifiedBy: 'admin',
  });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  // Fetch rollups from API
  const fetchRollups = async () => {
    setLoading(true);
    try {
      const response = await api.get('/adjustments/rollup');
      const transformed = (response.data.data || []).map(r => ({
        id: r.id,
        AdjustmentRollupCode: r.AdjustmentRollupCode,
        AdjustmentRollupName: r.AdjustmentRollupName,
        AdjustmentRollupType: r.AdjustmentRollupType,
        Year: r.Year,
        Period: r.Period,
        AdjustmentList: r.AdjustmentList || [],
      }));
      setRollups(transformed);
    } catch (error) {
      setNotification({ open: true, message: 'Failed to load adjustment rollups', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRollups();
    // Fetch valid master adjustment codes
    const fetchAdjustmentCodes = async () => {
      try {
        const response = await api.get('/adjustments/master');
        const codes = (response.data.data || []).map(a => a.AdjustmentCode?.trim().toUpperCase()).filter(Boolean);
        setValidAdjustmentCodes(codes);
      } catch (err) {
        setValidAdjustmentCodes([]);
      }
    };
    fetchAdjustmentCodes();
  }, []);

  // Table columns
  const columns = [
    {
      field: 'AdjustmentRollupCode',
      headerName: 'ROLLUP CODE',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span style={{ fontWeight: 'bold' }}>{params.value}</span>
      ),
    },
    {
      field: 'AdjustmentRollupName',
      headerName: 'NAME',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'AdjustmentRollupType',
      headerName: 'TYPE',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'Period',
      headerName: 'PERIOD',
      flex: 1,
      minWidth: 90,
      valueGetter: (params) => `${params.row.Year}-${params.row.Period}`,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)} title="Edit Rollup">
            <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row.id)} title="Delete Rollup">
            <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Handlers
  const handleSelectionChange = (newSelectionModel) => {
    setSelectedRows(newSelectionModel);
  };

  const handleAddNew = () => {
    setEditMode(false);
    setCurrentRollup({
      AdjustmentRollupCode: '',
      AdjustmentRollupName: '',
      AdjustmentRollupType: '',
      Year: new Date().getFullYear(),
      Period: 'Q4',
    });
    setOpenDialog(true);
  };

  const handleEdit = (rollup) => {
    setEditMode(true);
    setCurrentRollup({ ...rollup, _newAdjCode: '' });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this adjustment rollup?')) {
      try {
        await api.delete(`/adjustments/rollup/${id}`);
        fetchRollups();
        setNotification({ open: true, message: 'Adjustment rollup deleted', severity: 'success' });
      } catch (error) {
        setNotification({ open: true, message: 'Failed to delete rollup', severity: 'error' });
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected rollups?`)) {
      try {
        await Promise.all(selectedRows.map(id => api.delete(`/adjustments/rollup/${id}`)));
        fetchRollups();
        setSelectedRows([]);
        setNotification({ open: true, message: `Deleted ${selectedRows.length} rollups`, severity: 'success' });
      } catch (error) {
        setNotification({ open: true, message: 'Failed to delete rollups', severity: 'error' });
      }
    }
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  const handleClose = () => {
    setOpenDialog(false);
    setConfirmCloseOpen(false);
  };

  const handleSave = async () => {
    try {
      const data = {
        AdjustmentRollupCode: currentRollup.AdjustmentRollupCode,
        AdjustmentRollupName: currentRollup.AdjustmentRollupName,
        AdjustmentRollupType: currentRollup.AdjustmentRollupType,
        Year: currentRollup.Year,
        Period: currentRollup.Period,
      };
      if (editMode && currentRollup.id) {
        await api.put(`/adjustments/rollup/${currentRollup.id}`, {
          ...data,
          AdjustmentList: currentRollup.AdjustmentList || [],
          ModifiedBy: currentRollup.ModifiedBy || 'admin',
        });
        setNotification({ open: true, message: 'Rollup updated', severity: 'success' });
      } else {
        // Ensure AdjustmentList is [] and ModifiedOn is set for new rollups
        await api.post('/adjustments/rollup', {
          ...data,
          AdjustmentList: [],
          ModifiedOn: new Date().toISOString(),
          ModifiedBy: currentRollup.ModifiedBy || 'admin',
        });
        setNotification({ open: true, message: 'Rollup created', severity: 'success' });
      }
      setOpenDialog(false);
      fetchRollups();
    } catch (error) {
      setNotification({ open: true, message: 'Failed to save rollup', severity: 'error' });
    }
  };

  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotification({ ...notification, open: false });
  };

  return (
    <Box className="adjustment-management-container">
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleNotificationClose} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Main Content */}
      <Box className="main-content">
        {/* Toolbar */}
        <Box className="toolbar-container">
          <Box className="selection-info" sx={{ pl: 0 }}>
            <Typography variant="h6" className="page-title" sx={{ pl: 0, fontWeight: 'bold' }}>Manage Adjustment Rollups</Typography>
            {selectedRows.length > 0 && (
              <Typography variant="body2" className="selection-info" sx={{ ml: 2 }}>
                {selectedRows.length} row{selectedRows.length !== 1 ? 's' : ''} selected
              </Typography>
            )}
          </Box>
          <Box className="button-group">
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              className="action-button"
            >
              Add
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RemoveIcon />}
              onClick={handleDeleteSelected}
              disabled={selectedRows.length === 0}
              className="action-button"
              sx={{ '&.Mui-disabled': { opacity: 0.4 } }}
            >
              Remove
            </Button>
          </Box>
        </Box>
        {/* Data Table */}
        <Box className="table-container">
          {loading ? (
            <LinearProgress />
          ) : (
            <SearchableDataTable
              searchPlaceholder="Search rollups..."
              rows={rollups}
              columns={columns}
              loading={loading}
              pageSize={15}
              tableName="adjustment-rollups-table"
              defaultSortField="AdjustmentRollupName"
              defaultSortOrder="asc"
              onRefresh={fetchRollups}
              checkboxSelection={true}
              selectionModel={selectedRows}
              onSelectionModelChange={handleSelectionChange}
              height="100%"
              className="custom-datagrid"
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleClose}
        onCancel={() => setConfirmCloseOpen(false)}
      />
      <Dialog
        open={openDialog}
        onClose={handleRequestClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editMode ? 'Edit Adjustment Rollup' : 'Add New Adjustment Rollup'}</DialogTitle>
        <DialogContent className="dialog-content">
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rollup Code"
                value={currentRollup.AdjustmentRollupCode || ''}
                onChange={e => setCurrentRollup({ ...currentRollup, AdjustmentRollupCode: e.target.value })}
                required
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={currentRollup.AdjustmentRollupName || ''}
                onChange={e => setCurrentRollup({ ...currentRollup, AdjustmentRollupName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={editMode}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={currentRollup.AdjustmentRollupType || ''}
                  label="Type"
                  onChange={e => setCurrentRollup({ ...currentRollup, AdjustmentRollupType: e.target.value })}
                >
                  <MenuItem value="ETF">ETF</MenuItem>
                  <MenuItem value="Deferred">Deferred</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={currentRollup.Year || new Date().getFullYear()}
                onChange={e => setCurrentRollup({ ...currentRollup, Year: e.target.value })}
                required
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Period</InputLabel>
                <Select
                  value={currentRollup.Period || 'Q4'}
                  label="Period"
                  onChange={e => setCurrentRollup({ ...currentRollup, Period: e.target.value })}
                  required
                  disabled={editMode}
                >
                  <MenuItem value="Q1">Q1</MenuItem>
                  <MenuItem value="Q2">Q2</MenuItem>
                  <MenuItem value="Q3">Q3</MenuItem>
                  <MenuItem value="Q4">Q4</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Adjustment List Editing Section (Edit Mode Only) */}
            {editMode && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Adjustment List
                  </Typography>
                  {/* Show current list as chips */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {currentRollup.AdjustmentList && currentRollup.AdjustmentList.map((adj, idx) => (
                      <Paper key={idx} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>{adj}</Typography>
                        <IconButton size="small" color="error" onClick={() => {
                          setCurrentRollup({
                            ...currentRollup,
                            AdjustmentList: currentRollup.AdjustmentList.filter((_, i) => i !== idx)
                          });
                        }}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))}
                  </Box>
                  {/* Add new adjustment code */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      label="Add Code"
                      value={currentRollup._newAdjCode || ''}
                      onChange={e => {
                        setCurrentRollup({ ...currentRollup, _newAdjCode: e.target.value });
                        setAdjCodeError('');
                      }}
                      sx={{ width: 120 }}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      error={!!adjCodeError}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const code = (currentRollup._newAdjCode || '').trim().toUpperCase();
                        if (!code) return;
                        if (!validAdjustmentCodes.includes(code)) {
                          setAdjCodeError('Invalid adjustment code. Please select a valid code from master adjustments.');
                          return;
                        }
                        if ((currentRollup.AdjustmentList || []).map(c => c.toUpperCase()).includes(code)) {
                          setAdjCodeError('Code already added.');
                          return;
                        }
                        setCurrentRollup({
                          ...currentRollup,
                          AdjustmentList: [...(currentRollup.AdjustmentList || []), code],
                          _newAdjCode: ''
                        });
                        setAdjCodeError('');
                      }}
                      disabled={!(currentRollup._newAdjCode && currentRollup._newAdjCode.trim().length > 0)}
                      sx={{ minWidth: 80 }}
                    >
                      Add
                    </Button>
                    {adjCodeError && (
                      <Typography variant="body2" color="error" sx={{ ml: 2, alignSelf: 'center', fontWeight: 500 }}>
                        {adjCodeError}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            )}

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdjustmentRollups;
